import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Camera, LogIn, LogOut, Coffee, CheckCircle2, AlertCircle, ArrowLeft, RotateCw, IdCard, Sparkles } from "lucide-react";
import { logger } from "@/lib/logger";

/**
 * Public kiosk attendance terminal. Employees type their Employee ID, see
 * their current state and which punches are allowed, then capture a selfie
 * to record the event. No login.
 */

const ACTION_META = {
  clock_in:    { label: "Clock in",    icon: LogIn,  cls: "bg-emerald-600 hover:bg-emerald-700" },
  clock_out:   { label: "Clock out",   icon: LogOut, cls: "bg-zinc-900 hover:bg-zinc-800" },
  break_start: { label: "Start break", icon: Coffee, cls: "bg-amber-500 hover:bg-amber-600" },
  break_end:   { label: "End break",   icon: Coffee, cls: "bg-sky-600 hover:bg-sky-700" },
};

// clock_in / break_end require a 3-photo grooming check, captured in sequence.
const GROOMING_ACTIONS = ["clock_in", "break_end"];
const GROOMING_STEPS = [
  { key: "face", label: "your FACE", hint: "Look straight at the camera" },
  { key: "uniform", label: "your FULL UNIFORM", hint: "Step back so your uniform is visible" },
  { key: "shoes", label: "your SHOES", hint: "Point the camera down at your shoes" },
];

const STATE_LABEL = {
  out:        { text: "Not clocked in",  cls: "bg-zinc-200 text-zinc-700" },
  working:    { text: "Currently working", cls: "bg-emerald-100 text-emerald-800" },
  on_break:   { text: "On break",          cls: "bg-amber-100 text-amber-800" },
};

const AUTO_RESET_MS = 8000;

// Time-of-day buckets feed both the greeting and the rotating sub-message so
// the kiosk feels warm and never quite the same twice.
const GREETINGS = {
  morning: [
    "A fresh new morning is here.",
    "Wishing you a calm, cheerful start.",
    "Today is a clean slate — go make it beautiful.",
    "Quiet mornings, big plans — let's begin.",
    "Good morning, sunshine. Let's roll.",
  ],
  afternoon: [
    "Halfway through — keep the energy up!",
    "Hello, afternoon hero.",
    "One step at a time — you're doing great.",
    "Your hospitality makes someone's day. Truly.",
    "A warm afternoon to you and yours.",
  ],
  evening: [
    "Evening, friend. Almost there.",
    "Soft lights, soft hearts — finish strong.",
    "Thank you for taking care of our guests today.",
    "The day is winding down — and so should the stress.",
    "Cool evening breeze — and a kind smile from us.",
  ],
  night: [
    "Quiet night shift — we appreciate you.",
    "Stars are out and so are you. Thank you.",
    "Brave the night, we've got your back.",
    "Late night, big heart. Take care out there.",
    "Whisper-quiet halls and steady hands. You've got this.",
  ],
};

const TIPS = [
  "Stand in good light and look at the camera.",
  "A quick selfie keeps your attendance accurate.",
  "Have a great shift!",
  "Hospitality runs on small kindnesses — thank you.",
  "If you forgot your ID, ask your manager.",
];

function bucketForHour(h) {
  if (h < 5) return "night";
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  if (h < 21) return "evening";
  return "night";
}

function pickFrom(arr, seed) {
  // Stable pick based on a seed (so the same minute stays calm; new minute → new line)
  if (!arr || arr.length === 0) return "";
  return arr[seed % arr.length];
}

export default function Kiosk() {
  // step: 'id' -> employee types ID; 'actions' -> show buttons; 'camera' -> capture; 'done' -> success
  const [step, setStep] = useState("id");
  const [empId, setEmpId] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [chosenAction, setChosenAction] = useState(null);
  const [punchBusy, setPunchBusy] = useState(false);
  const [punchResult, setPunchResult] = useState(null);
  const [cameraError, setCameraError] = useState("");
  // Grooming (clock_in / break_end): collect 3 shots in sequence.
  const [groomingShots, setGroomingShots] = useState({ face: null, uniform: null, shoes: null });
  const [now, setNow] = useState(new Date());
  const [branding, setBranding] = useState({ company_name: "Happy Retreats", logo_path: null });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const resetTimerRef = useRef(null);

  // Fetch branding once — endpoint is public so no auth needed
  useEffect(() => {
    let cancelled = false;
    api.get("/branding").then(({ data }) => {
      if (!cancelled && data) setBranding(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Tick clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Greeting + tip rotate by the current minute, so each visitor sees a fresh line
  const greeting = useMemo(() => {
    const bucket = bucketForHour(now.getHours());
    const seed = now.getHours() * 60 + now.getMinutes();
    return {
      timeOfDay: bucket,
      title: { morning: "Good morning", afternoon: "Good afternoon", evening: "Good evening", night: "Good night" }[bucket],
      message: pickFrom(GREETINGS[bucket], seed),
      tip: pickFrom(TIPS, seed + 7),
    };
  }, [now]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const resetToIdStep = useCallback(() => {
    stopCamera();
    setEmployee(null);
    setEmpId("");
    setChosenAction(null);
    setPunchResult(null);
    setCameraError("");
    setGroomingShots({ face: null, uniform: null, shoes: null });
    setStep("id");
  }, [stopCamera]);

  useEffect(() => () => { stopCamera(); clearTimeout(resetTimerRef.current); }, [stopCamera]);

  // Auto-bounce back to ID step after success
  useEffect(() => {
    if (step !== "done") return;
    resetTimerRef.current = setTimeout(resetToIdStep, AUTO_RESET_MS);
    return () => clearTimeout(resetTimerRef.current);
  }, [step, resetToIdStep]);

  // Open the camera when we enter the camera step
  useEffect(() => {
    if (step !== "camera") return;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try { await videoRef.current.play(); } catch (e) { logger.debug("video.play", e); }
        }
      } catch (e) {
        setCameraError(e.message || "Could not open the camera. Allow camera access and try again.");
      }
    })();
    return () => { cancelled = true; };
  }, [step]);

  const lookupEmployee = async () => {
    const id = empId.trim();
    if (!id) return;
    setLookupBusy(true);
    setPunchResult(null);
    try {
      const { data } = await api.post("/kiosk/lookup", { employee_id: id });
      setEmployee(data);
      setStep("actions");
    } catch (e) {
      setPunchResult({ ok: false, message: e.response?.data?.detail || "Lookup failed" });
    } finally {
      setLookupBusy(false);
    }
  };

  const chooseAction = (action) => {
    setChosenAction(action);
    setCameraError("");
    setGroomingShots({ face: null, uniform: null, shoes: null });
    setStep("camera");
  };

  // Grab the current square video frame as a JPEG data URL.
  const grabFrame = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return null;
    const size = 640;
    c.width = size; c.height = size;
    const ctx = c.getContext("2d");
    const vw = v.videoWidth || size;
    const vh = v.videoHeight || size;
    const ratio = Math.min(vw, vh);
    ctx.drawImage(v, (vw - ratio) / 2, (vh - ratio) / 2, ratio, ratio, 0, 0, size, size);
    return c.toDataURL("image/jpeg", 0.82);
  };

  const submitPunch = async (body) => {
    setPunchBusy(true);
    try {
      const { data } = await api.post("/kiosk/punch", { employee_id: employee.employee_id, type: chosenAction, ...body });
      stopCamera();
      setPunchResult({ ok: true, ...data, action: chosenAction });
      setStep("done");
    } catch (e) {
      setPunchResult({ ok: false, message: e.response?.data?.detail || "Punch failed — try again" });
    } finally {
      setPunchBusy(false);
    }
  };

  const isGrooming = GROOMING_ACTIONS.includes(chosenAction);
  // Index of the next grooming shot to capture (0..2), or 3 when all done.
  const groomingIdx = GROOMING_STEPS.findIndex((s) => !groomingShots[s.key]);

  const capture = async () => {
    const frame = grabFrame();
    if (!frame) return;
    if (!isGrooming) {
      return submitPunch({ selfie_b64: frame });
    }
    // Grooming: store this shot; submit once all 3 are captured.
    const stepKey = GROOMING_STEPS[groomingIdx]?.key;
    if (!stepKey) return;
    const next = { ...groomingShots, [stepKey]: frame };
    setGroomingShots(next);
    if (next.face && next.uniform && next.shoes) {
      await submitPunch({ face_b64: next.face, uniform_b64: next.uniform, shoes_b64: next.shoes });
    }
  };

  // Big header / clock with company branding
  const Header = (
    <header className="w-full px-6 py-4 flex items-center justify-between border-b border-zinc-200 bg-white">
      <div className="flex items-center gap-3 min-w-0">
        {branding.logo_path_dark ? (
          <img
            src={`${API}/files?path=${encodeURIComponent(branding.logo_path_dark)}`}
            alt={branding.company_name}
            className="h-10 w-auto object-contain"
            data-testid="kiosk-logo"
            onError={(e) => { e.target.src = "/assets/happy-retreats-logo-dark.png"; }}
          />
        ) : (
          <img
            src="/assets/happy-retreats-logo-dark.png"
            alt={branding.company_name}
            className="h-10 w-auto object-contain"
            data-testid="kiosk-logo-bundled"
          />
        )}
        <div className="min-w-0">
          <div className="font-display font-black tracking-tight text-base sm:text-lg truncate" data-testid="kiosk-company-name">{branding.company_name}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Attendance Kiosk</div>
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono text-2xl sm:text-3xl tabular-nums" data-testid="kiosk-clock">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
        <div className="text-xs text-zinc-500">{now.toLocaleDateString([], { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</div>
      </div>
    </header>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-zinc-100 flex flex-col">
      {Header}

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-xl">
          {step === "id" && (
            <section className="bg-white border border-zinc-200 rounded-lg p-8 shadow-sm" data-testid="kiosk-step-id">
              <div className="flex flex-col items-center mb-6">
                <img
                  src={branding.logo_path_dark ? `${API}/files?path=${encodeURIComponent(branding.logo_path_dark)}` : "/assets/happy-retreats-logo-dark.png"}
                  alt={branding.company_name}
                  className="h-20 sm:h-24 w-auto object-contain"
                  onError={(e) => { e.target.src = "/assets/happy-retreats-logo-dark.png"; }}
                  data-testid="kiosk-hero-logo"
                />
              </div>
              <div className="flex items-center gap-2 mb-2 text-zinc-500 justify-center">
                <Sparkles className="w-4 h-4" />
                <span className="text-[10px] uppercase tracking-widest font-bold" data-testid="kiosk-greeting-eyebrow">{greeting.title}</span>
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-1 text-center" data-testid="kiosk-greeting-title">
                Welcome back!
              </h1>
              <p className="text-zinc-600 text-sm mb-1 italic text-center" data-testid="kiosk-greeting-message">
                {greeting.message}
              </p>
              <p className="text-zinc-500 text-sm mb-6 text-center">Enter your Employee ID to clock in or out.</p>
              <form
                onSubmit={(e) => { e.preventDefault(); lookupEmployee(); }}
                className="space-y-3"
              >
                <Input
                  value={empId}
                  onChange={(e) => setEmpId(e.target.value.toUpperCase())}
                  placeholder="e.g. AMA331"
                  className="h-14 text-lg font-mono uppercase tracking-widest"
                  autoFocus
                  data-testid="kiosk-empid-input"
                  inputMode="text"
                />
                <Button
                  type="submit"
                  disabled={lookupBusy || !empId.trim()}
                  className="w-full h-14 text-base bg-zinc-950 hover:bg-zinc-800"
                  data-testid="kiosk-continue"
                >
                  {lookupBusy ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                  Continue
                </Button>
              </form>
              {punchResult && !punchResult.ok && (
                <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 flex items-center gap-2" data-testid="kiosk-lookup-error">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {punchResult.message}
                </div>
              )}
              <p className="text-[11px] text-zinc-400 mt-6 text-center" data-testid="kiosk-tip">{greeting.tip}</p>
            </section>
          )}

          {step === "actions" && employee && (
            <section className="bg-white border border-zinc-200 rounded-lg p-7 shadow-sm space-y-5" data-testid="kiosk-step-actions">
              <button
                onClick={resetToIdStep}
                className="text-xs text-zinc-500 inline-flex items-center gap-1 hover:text-zinc-900"
                data-testid="kiosk-back"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Wrong ID? Go back
              </button>
              <div className="flex items-center gap-4">
                {employee.avatar_path ? (
                  <img
                    src={`${API}/files?path=${encodeURIComponent(employee.avatar_path)}`}
                    alt=""
                    className="w-20 h-20 rounded-full object-cover border-2 border-zinc-200"
                    data-testid="kiosk-employee-avatar"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-zinc-100 border-2 border-zinc-200 flex items-center justify-center text-zinc-400 font-display font-bold text-xl">
                    {(employee.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-2xl font-bold tracking-tight truncate" data-testid="kiosk-employee-name">{employee.name}</div>
                  <div className="text-xs text-zinc-500">{employee.role} · {employee.employee_id}</div>
                  <span className={`inline-block mt-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${STATE_LABEL[employee.current_state]?.cls || "bg-zinc-200 text-zinc-700"}`} data-testid="kiosk-employee-state">
                    {STATE_LABEL[employee.current_state]?.text || employee.current_state}
                  </span>
                </div>
              </div>
              {employee.allowed_actions.length === 0 ? (
                <div className="text-sm text-zinc-500 italic">No further actions available right now.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {employee.allowed_actions.map((a) => {
                    const meta = ACTION_META[a];
                    if (!meta) return null;
                    const Icon = meta.icon;
                    return (
                      <Button
                        key={a}
                        onClick={() => chooseAction(a)}
                        className={`h-16 text-base font-semibold text-white ${meta.cls}`}
                        data-testid={`kiosk-action-${a}`}
                      >
                        <Icon className="w-5 h-5 mr-2" /> {meta.label}
                      </Button>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {step === "camera" && employee && chosenAction && (
            <section className="bg-white border border-zinc-200 rounded-lg p-6 shadow-sm space-y-4" data-testid="kiosk-step-camera">
              <div>
                <div className="text-xs text-zinc-500 uppercase tracking-widest">{employee.name} · {ACTION_META[chosenAction]?.label}</div>
                {isGrooming ? (
                  <>
                    <div className="font-display text-xl font-bold mt-0.5" data-testid="kiosk-grooming-prompt">
                      Photo {Math.min(groomingIdx + 1, 3)} of 3 — capture {GROOMING_STEPS[Math.min(groomingIdx, 2)]?.label}
                    </div>
                    <div className="text-sm text-zinc-500">{GROOMING_STEPS[Math.min(groomingIdx, 2)]?.hint}</div>
                    <div className="flex gap-1.5 mt-2" data-testid="kiosk-grooming-progress">
                      {GROOMING_STEPS.map((s) => (
                        <span key={s.key} className={`h-1.5 flex-1 rounded-full ${groomingShots[s.key] ? "bg-emerald-500" : "bg-zinc-200"}`} />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="font-display text-xl font-bold mt-0.5">Look at the camera</div>
                )}
              </div>
              <div className="relative aspect-square w-full max-w-sm mx-auto rounded-lg overflow-hidden bg-zinc-900 border border-zinc-300">
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  data-testid="kiosk-video"
                />
                <div className="absolute inset-0 ring-4 ring-white/30 rounded-lg pointer-events-none" />
              </div>
              {cameraError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 flex items-center gap-2" data-testid="kiosk-camera-error">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {cameraError}
                </div>
              )}
              {punchResult && !punchResult.ok && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 flex items-center gap-2" data-testid="kiosk-punch-error">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {punchResult.message}
                </div>
              )}
              <canvas ref={canvasRef} className="hidden" />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { stopCamera(); setStep("actions"); }} className="h-12" data-testid="kiosk-camera-cancel">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button
                  onClick={capture}
                  disabled={punchBusy || !!cameraError}
                  className="flex-1 h-12 bg-zinc-950 hover:bg-zinc-800"
                  data-testid="kiosk-capture"
                >
                  {punchBusy ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Camera className="w-5 h-5 mr-2" />}
                  {isGrooming
                    ? (groomingIdx >= 2 ? `Capture & ${ACTION_META[chosenAction]?.label.toLowerCase()}` : `Capture ${GROOMING_STEPS[Math.max(groomingIdx, 0)]?.label.toLowerCase()}`)
                    : `Capture & ${ACTION_META[chosenAction]?.label.toLowerCase()}`}
                </Button>
              </div>
            </section>
          )}

          {step === "done" && punchResult && punchResult.ok && (
            <section className="bg-white border border-emerald-300 rounded-lg p-10 shadow-sm text-center space-y-3" data-testid="kiosk-step-done">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" strokeWidth={1.5} />
              <h2 className="font-display text-3xl font-black tracking-tight" data-testid="kiosk-done-message">
                {ACTION_META[punchResult.action]?.label} successful
              </h2>
              <p className="text-zinc-500">Have a great {new Date().getHours() < 17 ? "day" : "evening"}, {punchResult.name?.split(" ")[0]}!</p>
              <Button onClick={resetToIdStep} variant="outline" className="mt-3" data-testid="kiosk-done-continue">
                <RotateCw className="w-4 h-4 mr-1" /> Continue (auto in {Math.ceil(AUTO_RESET_MS / 1000)}s)
              </Button>
            </section>
          )}
        </div>
      </main>

      <footer className="px-6 py-3 text-center text-[11px] text-zinc-400 border-t border-zinc-200 bg-white" data-testid="kiosk-footer">
        Attendance Kiosk · v1 · Powered by {branding.company_name}
      </footer>
    </div>
  );
}
